import type {
  BenchmarkRun,
  BenchmarkReport,
  BenchmarkProgress,
  CreateRunRequest,
  BenchmarkRunCreate,
  BenchmarkDocument,
  BenchmarkStatsByTag,
  DocumentDrilldown,
  RunComparison,
  StabilityScore,
} from '../types/benchmark';
import { authFetch, handleSessionExpired } from './authService';

const API_BASE = `${import.meta.env.VITE_API_URL || 'http://localhost:8001'}/api/v1`;

async function handleResponse<T>(response: Response): Promise<T> {
  if (response.status === 401) {
    // authFetch a déjà tenté le refresh — session réellement expirée.
    handleSessionExpired(response);
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Erreur réseau' }));
    throw new Error(error.detail || 'Erreur réseau');
  }

  return response.json();
}

export const benchmarkService = {
  getRuns: async (tags?: string[]): Promise<BenchmarkRun[]> => {
    const params = new URLSearchParams();
    if (tags && tags.length > 0) {
      tags.forEach(tag => params.append('tags', tag));
    }
    const response = await authFetch(`${API_BASE}/benchmark/runs?${params}`);
    return handleResponse<BenchmarkRun[]>(response);
  },

  getRun: async (id: string): Promise<BenchmarkRun> => {
    const response = await authFetch(`${API_BASE}/benchmark/runs/${id}`);
    return handleResponse<BenchmarkRun>(response);
  },

  getReport: async (id: string): Promise<BenchmarkReport> => {
    const response = await authFetch(`${API_BASE}/benchmark/runs/${id}/report`);
    return handleResponse<BenchmarkReport>(response);
  },

  // PROD1.7.8 N1 — 3 séries d'agrégats (global / standards / complexes)
  getStatsByTag: async (id: string): Promise<BenchmarkStatsByTag> => {
    const response = await authFetch(`${API_BASE}/benchmark/runs/${id}/stats`);
    return handleResponse<BenchmarkStatsByTag>(response);
  },

  getProgress: async (id: string): Promise<BenchmarkProgress> => {
    const response = await authFetch(`${API_BASE}/benchmark/runs/${id}/progress`);
    return handleResponse<BenchmarkProgress>(response);
  },

  createRun: async (data: CreateRunRequest | BenchmarkRunCreate): Promise<BenchmarkRun> => {
    const response = await authFetch(`${API_BASE}/benchmark/runs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleResponse<BenchmarkRun>(response);
  },

  executeRun: async (runId: string): Promise<{ status: string; message: string }> => {
    const response = await authFetch(`${API_BASE}/benchmark/runs/${runId}/execute`, {
      method: 'POST',
    });
    return handleResponse<{ status: string; message: string }>(response);
  },

  getDocuments: async (runId: string): Promise<BenchmarkDocument[]> => {
    const response = await authFetch(`${API_BASE}/benchmark/runs/${runId}/documents`);
    return handleResponse<BenchmarkDocument[]>(response);
  },

  getDocumentDrilldown: async (runId: string, docId: number): Promise<DocumentDrilldown> => {
    const response = await authFetch(`${API_BASE}/benchmark/runs/${runId}/documents/${docId}`);
    return handleResponse<DocumentDrilldown>(response);
  },

  getLlmLogs: async (runId: string, docId: number): Promise<{ input: string; output: string }> => {
    const response = await authFetch(`${API_BASE}/benchmark/runs/${runId}/documents/${docId}/llm-logs`);
    const data = await handleResponse<{ llm_input: string; llm_output: string }>(response);
    return {
      input: data.llm_input || '',
      output: data.llm_output || ''
    };
  },

  exportCsv: async (runId: string): Promise<Blob> => {
    const response = await authFetch(`${API_BASE}/benchmark/runs/${runId}/export?format=csv`);
    if (!response.ok) {
      throw new Error('Failed to export CSV');
    }
    return response.blob();
  },

  exportExcel: async (runId: string): Promise<Blob> => {
    const response = await authFetch(`${API_BASE}/benchmark/runs/${runId}/export?format=xlsx`);
    if (!response.ok) {
      throw new Error('Failed to export Excel');
    }
    return response.blob();
  },

  compareRuns: async (runAId: string, runBId: string): Promise<RunComparison> => {
    const response = await authFetch(`${API_BASE}/benchmark/compare?run_a=${runAId}&run_b=${runBId}`);
    return handleResponse<RunComparison>(response);
  },

  getStability: async (runAId: string, runBId: string): Promise<StabilityScore> => {
    const response = await authFetch(`${API_BASE}/benchmark/stability?run_a=${runAId}&run_b=${runBId}`);
    return handleResponse<StabilityScore>(response);
  },

  lockRun: async (runId: string, locked: boolean): Promise<void> => {
    const response = await authFetch(`${API_BASE}/benchmark/runs/${runId}/lock?is_locked=${locked}`, {
      method: 'PATCH',
    });
    return handleResponse(response);
  },

  deleteRun: async (runId: string): Promise<void> => {
    const response = await authFetch(`${API_BASE}/benchmark/runs/${runId}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },

  cancelRun: async (runId: string): Promise<{ status: string; message: string }> => {
    const response = await authFetch(`${API_BASE}/benchmark/runs/${runId}/cancel`, {
      method: 'POST',
    });
    return handleResponse<{ status: string; message: string }>(response);
  },

  cloneRun: async (runId: string): Promise<{ run_id: string }> => {
    const response = await authFetch(`${API_BASE}/benchmark/runs/${runId}/clone`, {
      method: 'POST',
    });
    return handleResponse(response);
  },

  getTags: async (): Promise<string[]> => {
    const response = await authFetch(`${API_BASE}/benchmark/tags`);
    return handleResponse<string[]>(response);
  },

  downloadAllLogs: async (runId: string): Promise<void> => {
    const response = await authFetch(`${API_BASE}/benchmark/runs/${runId}/download-logs`);

    if (response.status === 401) {
      // authFetch a déjà tenté le refresh — session réellement expirée.
      handleSessionExpired(response);
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to download logs' }));
      throw new Error(error.detail || 'Failed to download logs');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `run-${runId.slice(0, 8)}-logs.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};
